import { parentRefToEntityType } from '@bower/shared/constants/parent-validity';
import type {
  ApiResponse,
  AttachmentOcrResponseData,
  MeetingSummary,
  ParentRef,
  RetryTaskRequest,
  SpeakerTurn,
  VideoInsightMode,
  VideoInsightRequest,
  VideoInsightsMetadata,
} from '@bower/shared';
import type { HttpClient } from './http-client.types';

export interface AttachmentTranscriptionResponseData {
  readonly transcript: string;
  readonly confidence?: number;
  readonly alternatives?: ReadonlyArray<{
    readonly transcript: string;
    readonly confidence?: number;
  }>;
  readonly language?: string | null;
  readonly speakerTurns?: SpeakerTurn[];
  readonly speakerCount?: number;
  readonly diarizationApplied?: boolean;
  readonly meetingSummary?: MeetingSummary;
  readonly processingTime?: number;
  readonly traceId?: string;
  readonly attachmentId?: string;
  readonly modelInfo?: { readonly model: string; readonly provider: string };
  readonly noSpeechDetected?: boolean;
}

export function uploadAttachment(
  http: HttpClient,
  formData: FormData,
  config?: { onUploadProgress?: (progressEvent: unknown) => void; signal?: AbortSignal },
) {
  return http.request(
    {
      method: 'POST',
      url: '/attachments/upload',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: config?.onUploadProgress as ((e: unknown) => void) | undefined,
      signal: config?.signal,
      timeout: 60000,
    },
    0,
  );
}

export async function downloadAttachmentBuffer(
  http: HttpClient,
  artifactId: string,
  params?: Record<string, string>,
): Promise<ArrayBuffer> {
  const response = await http.getClient().get(`/attachments/${artifactId}/download`, {
    responseType: 'arraybuffer',
    params,
  });
  return response.data as ArrayBuffer;
}

export async function downloadInlineImageBuffer(
  http: HttpClient,
  storagePath: string,
): Promise<ArrayBuffer> {
  const response = await http.getClient().get('/upload/download-inline-image', {
    responseType: 'arraybuffer',
    params: { storagePath },
  });
  return response.data as ArrayBuffer;
}

/** Shape returned by `GET /attachments/:id/url` (attachment.upload.controller). */
export interface AttachmentUrlResponseData {
  readonly url: string;
  readonly expiresAt: string;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly fileSize?: number;
}

export function getAttachmentUrl(
  http: HttpClient,
  artifactId: string,
  disposition?: 'attachment',
): Promise<ApiResponse<AttachmentUrlResponseData>> {
  const query = disposition ? `?disposition=${disposition}` : '';
  return http.get<AttachmentUrlResponseData>(`/attachments/${artifactId}/url${query}`);
}

/** A refused archive, with the ids the server could not include when it says. */
export class AttachmentArchiveError extends Error {
  constructor(
    readonly code: string,
    readonly unavailableIds: ReadonlyArray<string>,
  ) {
    super(`Archive download failed: ${code}`);
    this.name = 'AttachmentArchiveError';
  }
}

/**
 * The zip request's ceiling, Cloud Run's own request timeout. The client's
 * 30 s default bounds the whole body, so it cut off any zip that took longer
 * to arrive, well inside the server's 2 GB cap.
 */
export const ATTACHMENT_ARCHIVE_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Several attachments as one zip. A Blob, like the audit export: the request
 * carries the auth and workspace headers, which a plain navigation cannot.
 */
export async function downloadAttachmentArchive(
  http: HttpClient,
  artifactIds: ReadonlyArray<string>,
): Promise<Blob> {
  try {
    const response = await http
      .getClient()
      .post(
        '/attachments/archive',
        { artifactIds },
        { responseType: 'blob', timeout: ATTACHMENT_ARCHIVE_TIMEOUT_MS },
      );
    return response.data as Blob;
  } catch (error) {
    // With `responseType: 'blob'` the JSON error body arrives as a Blob too.
    const body: unknown = (error as { response?: { data?: unknown } }).response?.data;
    const parsed: unknown =
      body instanceof Blob
        ? await body
            .text()
            .then(JSON.parse)
            .catch(() => null)
        : null;
    const detail = (
      parsed as {
        error?: { code?: string; details?: { artifactIds?: string[] } };
      } | null
    )?.error;
    throw new AttachmentArchiveError(detail?.code ?? 'UNKNOWN', detail?.details?.artifactIds ?? []);
  }
}

export function getAnnotations(http: HttpClient, artifactId: string) {
  return http.get(`/attachments/${artifactId}/annotations`);
}

export function getProtocolVideoBinding(http: HttpClient, protocolArtifactId: string) {
  return http.get(`/protocols/${protocolArtifactId}/video-binding`);
}

export function putProtocolVideoBinding(
  http: HttpClient,
  protocolArtifactId: string,
  binding: unknown,
) {
  return http.put(`/protocols/${protocolArtifactId}/video-binding`, binding);
}

export function deleteProtocolVideoBinding(http: HttpClient, protocolArtifactId: string) {
  return http.delete(`/protocols/${protocolArtifactId}/video-binding`);
}

export function saveAnnotations(http: HttpClient, artifactId: string, annotations: unknown) {
  return http.put(`/attachments/${artifactId}/annotations`, { annotations });
}

export function getInlineAnnotations(http: HttpClient, noteId: string, imageKey: string) {
  return http.get(
    `/artifacts/${noteId}/inline-annotations?imageKey=${encodeURIComponent(imageKey)}`,
  );
}

export function saveInlineAnnotation(
  http: HttpClient,
  noteId: string,
  imageKey: string,
  annotations: unknown,
) {
  return http.put(`/artifacts/${noteId}/inline-annotations`, { imageKey, annotations });
}

export function copyInlineImage(
  http: HttpClient,
  sourceUrl: string,
  noteId: string,
): Promise<ApiResponse<{ url: string }>> {
  return http.post<{ url: string }>('/upload/copy-inline-image', { sourceUrl, noteId });
}

export function getInlineDiagram(http: HttpClient, noteId: string, diagramId: string) {
  return http.get(
    `/artifacts/${noteId}/inline-diagrams?diagramId=${encodeURIComponent(diagramId)}`,
  );
}

export function saveInlineDiagram(
  http: HttpClient,
  noteId: string,
  diagramId: string,
  diagram: unknown,
) {
  return http.put(`/artifacts/${noteId}/inline-diagrams`, { diagramId, diagram });
}

export function processAttachmentOCR(
  http: HttpClient,
  artifactId: string,
  options?: {
    extractAsTable?: boolean;
    outputFormat?: 'markdown' | 'text';
    provider?: string;
    model?: string;
  },
  workspaceId?: string,
): Promise<ApiResponse<AttachmentOcrResponseData>> {
  return http.request<AttachmentOcrResponseData>(
    {
      method: 'POST',
      url: `/attachments/${artifactId}/ocr`,
      data: options ?? {},
      ...(workspaceId && { headers: { 'x-workspace-id': workspaceId } }),
      timeout: 120000,
    },
    0,
  );
}

/**
 * POST /attachments/:id/retry-task: start a background task on an attachment
 * from its row menu: `note-ocr` (Image to Text) or `note-transcription`
 * (Transcribe as dictation / meeting). 202 `{ requestedAt }`; 409
 * `TASK_ACTIVE` when one is already queued or running. No client retry: a 5xx
 * after the slot was written would only meet `TASK_ACTIVE`.
 */
export function retryAttachmentTask(
  http: HttpClient,
  workspaceId: string,
  artifactId: string,
  body: RetryTaskRequest,
): Promise<ApiResponse<{ requestedAt: string }>> {
  return http.request<{ requestedAt: string }>(
    {
      method: 'POST',
      url: `/attachments/${artifactId}/retry-task`,
      data: body,
      headers: { 'x-workspace-id': workspaceId },
    },
    0,
  );
}

export function processAttachmentTranscription(
  http: HttpClient,
  workspaceId: string,
  artifactId: string,
  options?: { language?: string; format?: 'text' | 'vtt'; multipleSpeakers?: boolean },
): Promise<ApiResponse<AttachmentTranscriptionResponseData>> {
  return http.request<AttachmentTranscriptionResponseData>(
    {
      method: 'POST',
      url: `/attachments/${artifactId}/transcribe`,
      data: options ?? {},
      headers: { 'x-workspace-id': workspaceId },
      timeout: 600000,
    },
    0,
  );
}

/** GET /attachments/:videoArtifactId/video-insights — stored proposals. */
export async function getVideoInsights(http: HttpClient, videoArtifactId: string) {
  return http.get<{ insights: VideoInsightsMetadata }>(
    `/attachments/${videoArtifactId}/video-insights`,
  );
}

/** POST /attachments/:videoArtifactId/video-insights — enqueue one mode (202). */
export async function requestVideoInsight(
  http: HttpClient,
  videoArtifactId: string,
  body: VideoInsightRequest,
) {
  return http.post<{
    accepted: true;
    mode: VideoInsightMode;
    requestedAt: string;
  }>(`/attachments/${videoArtifactId}/video-insights`, body);
}

export function getAttachments(http: HttpClient, parent: ParentRef) {
  // Serialise the ParentRef to the wire string at the boundary. `artifactAny`
  // → bare 'artifact', which the backend normalises to match every dotted
  // 'artifact.<subtype>' row (so a note's attachments are found).
  const parentEntityType = parentRefToEntityType(parent);
  return http.get(
    `/artifacts?parentEntityType=${parentEntityType}&parentEntityId=${parent.id}&artifactType=attachment`,
  );
}
